import { inject, Injectable } from '@angular/core';
import { LOCAL_STORAGE_CONSTANT } from '@constants/localstorage.constant';
import { StoreScopedWorker } from '@db-models/store-scope.model';
import { AlertToastrService } from '@util-services/alert-toastr.service';
import { CryptoService } from '@util-services/crypto.service';
import { LocalStorageService } from '@util-services/local-storage.service';
import { callerCanActOnWorker } from '@util-services/store-scope.util';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {

  protected featuresList: { [key: string]: boolean | number } = {};
  protected masterFeaturesList: { requiredSubscriptionPlan: number }[] = [];

  private alertToastrService = inject(AlertToastrService);
  private localStorageService = inject(LocalStorageService);
  private cryptoService = inject(CryptoService);

  setUserPermissions(permissions: string[]): void {
    this.localStorageService.set(LOCAL_STORAGE_CONSTANT.USER_PERMISSIONS, this.cryptoService.encryptValue(JSON.stringify(permissions)));
  }

  // Same cipher-keyed memo as WorkerService.getLoggedInWorkerFromLocalStorage(). Assigned only
  // after JSON.parse succeeds, so a corrupt blob never poisons it.
  private cachedPermissionsCipher: string = null;
  private cachedPermissions: string[] = null;

  getUserPermissions(): string[] {
    const encryptedString: string = this.localStorageService.get(LOCAL_STORAGE_CONSTANT.USER_PERMISSIONS);
    if (encryptedString) {
      if (encryptedString !== this.cachedPermissionsCipher) {
        const decryptedString: string = this.cryptoService.decryptValue(encryptedString);
        const permissions = JSON.parse(decryptedString);
        this.cachedPermissions = permissions;
        this.cachedPermissionsCipher = encryptedString;
      }
      return [...this.cachedPermissions];
    } else {
      this.cachedPermissionsCipher = null;
      this.cachedPermissions = null;
      return [];
    }
  }

  // Called from AuthService: logout, handlePartnerSwitch, handleAuthCallback error branch.
  clearUserPermissionsCache(): void {
    this.cachedPermissionsCipher = null;
    this.cachedPermissions = null;
  }

  /**
   * Permissions are captured once at login and are not re-read until the user logs
   * in again, so a session opened before a scope was renamed still holds the
   * superseded name. Treating the superseded name as granting the current one keeps
   * those sessions working instead of hiding the affected screens until re-login.
   *
   * Remove this map and getEffectivePermissions() one release after the rename ships.
   */
  private static readonly SUPERSEDED_SCOPES: Readonly<Record<string, string>> = {
    'read:own:store': 'read:own:stores',
    'add:own:store': 'add:own:stores',
    'edit:own:store': 'edit:own:stores',
    'delete:own:store': 'delete:own:stores',
  };

  /**
   * Stored permissions plus the current name of any superseded scope they contain.
   *
   * Exposed so a caller resolving MANY permissions can build the expanded set once per call
   * site: getUserPermissions() itself is now cheap (cipher-keyed memo), so this only saves
   * re-running the supersession expansion, not a decrypt.
   *
   * Expanding the granted set (rather than the requested one) keeps both the OR and
   * the AND branch of hasPermission() correct without either needing to know.
   */
  getEffectivePermissions(): string[] {
    const permissions = this.getUserPermissions();
    const currentNames = permissions
      .map(permission => PermissionService.SUPERSEDED_SCOPES[permission])
      .filter(currentName => !!currentName && !permissions.includes(currentName));

    return currentNames.length ? [...permissions, ...currentNames] : permissions;
  }

  /**
   * Checks if the user has the specified permission(s).
   *
   * @param value - A single permission or an array of permissions to check.
   * @param filterModeAND - If true, all permissions in the array must be present (AND behavior).
   *                        If false, at least one permission in the array must be present (OR behavior).
   *                        Defaults to false.
   * @returns `true` if the user has the required permission(s), otherwise `false`.
   */
  hasPermission(value: string | string[], filterModeAND = false): boolean {
    if (!value) {
      return true;
    }

    if (typeof value === 'string') {
      value = [value.toString()];
    }

    const permissions = this.getEffectivePermissions();
    const userPermissions = value as string[];

    if (filterModeAND) {
      // AND behavior: All permissions must be present
      return userPermissions.every(permission => permissions.includes(permission));
    } else {
      // OR behavior: At least one permission must be present
      return userPermissions.some(permission => permissions.includes(permission));
    }
  }

  /**
   * May the current user edit this worker / resource?
   *
   * The three scopes are OR'd exactly as the worker update is gated: a partner-wide
   * editor, the worker editing himself, or a store-scoped editor whose store matches.
   * `worker` is undefined on the create form, where neither "own" scope can match.
   *
   * Resolved in ONE pass on purpose: hasPermission() re-reads local storage and runs an
   * AES decrypt per call, and the employee/resource forms ask this on every
   * change-detection cycle - asking three times there cost three decrypts a cycle.
   *
   * The store branch mirrors cakephp workersShareStore(..., includeDescendants: true). The caller's
   * visible store set is a parameter, not an injected service, to keep DI acyclic (StoreScopeService
   * injects this service); null (the default) means the Store Hierarchy add-on is off.
   */
  canEditWorker(
    worker: StoreScopedWorker | undefined,
    loggedInWorker: StoreScopedWorker | undefined,
    hierarchyVisibleStoreIds: ReadonlySet<number> | null = null,
  ): boolean {
    const permissions = this.getEffectivePermissions();

    return permissions.includes('edit:resources')
      || (!!worker && permissions.includes('edit:own:resources') && worker.id === loggedInWorker?.id)
      // edit:profile is the self-service scope, and PUT /api/v1/workers/:uuid accepts it
      // (config/routes.php: edit:resources|edit:profile|edit:own:store:resources). Without it here
      // a holder of edit:profile alone met a read-only form telling him he may not edit a profile
      // the API would have saved. Deliberately narrower than the route: own row only. The backend
      // currently accepts an edit:profile-only caller for ANY worker of the partner, because its
      // refusal requires edit:own:store:resources to be present - see WorkersService
      // ::updateWorkerByRequest(). That is its own ticket; the UI must not advertise it.
      || (!!worker && permissions.includes('edit:profile') && worker.id === loggedInWorker?.id)
      || (!!worker && permissions.includes('edit:own:store:resources') && callerCanActOnWorker(loggedInWorker, worker, hierarchyVisibleStoreIds));
  }

  permissionNotAvailable(): void {
    this.alertToastrService.showError('general.403_error_message');
  }
}
